import React, { useEffect, useState, useMemo } from "react";
import PermissionButton from "../../shared/PermissionButton";
import {
  useAssessmentReportValues,
  useFinalizedAssessmentYearLevel,
  useLoggedInRecruitmentId,
  useReportGeneration,
  useReportPublish,
  useReportSetupTemplate,
  useReportSetupYearLevel,
  useStudentWithAssessmentRoutine,
  useSystemExamSubjects,
} from "../../../hooks/usePublishAssessment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { Spinner } from "reactstrap";
import Button from "../../shared/Button";
import Select from "../../shared/Select";
import { Switch } from "@material-ui/core";
import ReportLedger from "./ReportLedger";
import { checkWidgetAdminOrNot } from "../../../../../utils/Constants";
import CustomModal from "../../shared/CustomModal";
import ReportTable from "./ReportTable";
import { NepaliDatePicker } from "nepali-calander";
import swal from "sweetalert";

const PublishAssessmentReport = ({ name }) => {
  const { data: finalizedAssessmentsYearLevel, loading } =
    useFinalizedAssessmentYearLevel();
  const { loggedInRecruitmentId } = useLoggedInRecruitmentId();
  const { data: templates } = useReportSetupTemplate();
  const { subjects, setSubjects, selectSystemExamSubjects } =
    useSystemExamSubjects();
  const {
    students,
    setStudents,
    getStudentWithAssessmentRoutine,
    loading: isStudentsLoading,
  } = useStudentWithAssessmentRoutine();
  const { reportValues, getAssessmentReportValues } =
    useAssessmentReportValues();
  const { reportSetups, selectByReportSetupYearLevel } =
    useReportSetupYearLevel();
  const { reportGenerating, generateReportTemplate, generateHtml } =
    useReportGeneration();
  const {
    reportPublishing,
    publishPdf,
    generateReportTemplate: generateReport,
  } = useReportPublish();
  const [activeTab, setActiveTab] = useState(null);
  const [isOpen, setIsOpen] = useState(null);
  const [isPercent, setIsPercent] = useState(false);
  const [printInfo, setprintInfo] = useState({
    yearLeveName: "",
    assessmentName: "",
    classroomName: "",
  });
  const [formData, setFormData] = useState({
    assessmentId: "",
    reportTemplateId: "",
    yearLevelId: "",
    classroomId: "",
    educationLevelId: "",
    educationLevelDivisionId: "",
  });
  const [publishDate, setPublishDate] = useState("");
  const [selectedPublishedDate, setSelectedPublishedDate] = useState("");
  const [recruitmentId, setRecruitmentId] = useState(null);
  const [selectAllStudents, setSelectAllStudents] = useState(false);
  const [calendarMode, setCalendarMode] = useState(
    localStorage.getItem("calendar-mode"),
  );
  const [isAggregated, setIsAggregated] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [reportType, setReportType] = useState(null);

  //extract classrooms from finalizedAssessmentYearLevel
  const classrooms = useMemo(() => {
    if (!activeTab) return [];

    return (
      finalizedAssessmentsYearLevel?.find(
        (yearlevel) => yearlevel.id === activeTab,
      )?.classroomYearLevelRecruitmentRoom ?? []
    );
  }, [activeTab, finalizedAssessmentsYearLevel]);

  //extract assessment form selected classrooms
  const assessments = useMemo(() => {
    if (classrooms?.length == 0 || !isOpen) return [];

    const selectedAssessments =
      classrooms?.find((classroom) => classroom?.id === isOpen)?.assessments ??
      [];
    return selectedAssessments?.map((item) => ({
      label: item?.assessmentName,
      value: item?.id,
      educationLevelDivisionId: item?.educationLevelDivisionId,
      educationLevelId: item?.educationLevelId,
      hasAggregationConfig: item?.hasAggregationConfig,
    }));
  }, [isOpen, classrooms]);

  //check if assessment has aggregation or not
  const hasAggregation = useMemo(() => {
    if (assessments?.length === 0) return false;

    return assessments?.find((item) => item?.value === formData?.assessmentId)
      ?.hasAggregationConfig;
  }, [formData?.assessmentId, assessments]);

  //template options
  const templateOptions = useMemo(() => {
    if (!templates?.length && !reportSetups?.length) return [];

    const watermarkTemplate = templates
      ?.filter((item) => item?.hasWatermark)
      .map((item) => ({
        label: item?.name,
        value: item?.id,
      }));
    const withoutWatermarkTemplate = templates
      ?.filter((item) => !item?.hasWatermark)
      .map((item) => ({
        label: item?.name,
        value: item?.id,
      }));

    const reportSetupTemplate = reportSetups?.map((item) => ({
      label: item?.name,
      value: item?.id,
      recruitmentId: item?.recruitmentId,
    }));

    if (reportSetupTemplate?.length) {
      return [
        {
          label: "Report Setup Template",
          options: reportSetupTemplate,
        },
        {
          label: "Templates With Watermark",
          options: watermarkTemplate,
        },
        {
          label: "Templates Without Watermark",
          options: withoutWatermarkTemplate,
        },
      ];
    }

    return [
      {
        label: "Templates With Watermark",
        options: watermarkTemplate,
      },
      {
        label: "Templates Without Watermark",
        options: withoutWatermarkTemplate,
      },
    ];
  }, [templates]);

  //get selected template
  const selectedTemplate = useMemo(() => {
    if (!templates?.length) return;
    return templates?.find((item) => item?.id === formData?.reportTemplateId);
  }, [formData?.reportTemplateId, templates]);

  //handle accordion
  const handleOpenAccordion = (classroomId) => {
    const openId = isOpen === classroomId ? null : classroomId;
    setIsOpen(openId);
    setSelectAllStudents(false);
    setStudents([]);
    setSubjects([]);
    setFormData({
      assessmentId: "",
      reportTemplateId: "",
      yearLevelId: "",
      classroomId: "",
      educationLevelId: "",
      educationLevelDivisionId: "",
    });
    return;
  };

  //handle select change
  const handleSelectChange = (option, type) => {
    if (type === "assessments") {
      setFormData((prev) => ({
        ...prev,
        reportTemplateId: "",
        assessmentId: option?.value,
        educationLevelDivisionId: option?.educationLevelDivisionId,
        educationLevelId: option?.educationLevelId,
      }));

      //prepare info for ledger
      const yearLeveName = finalizedAssessmentsYearLevel?.find(
        (item) => item?.id === formData?.yearLevelId,
      )?.name;
      const classroomName = classrooms?.find(
        (item) => item?.id === formData?.classroomId,
      )?.name;
      setprintInfo((prev) => ({
        assessmentName: option?.label,
        yearLeveName,
        classroomName,
      }));

      //prepare params for fetching subjects
      const params = {
        assessmentId: option?.value,
        educationLevelDivisionId: option?.educationLevelDivisionId,
        educationLevelId: option?.educationLevelId,
        classroomId: formData?.classroomId,
        yearLevelId: formData?.yearLevelId,
      };
      selectSystemExamSubjects(params);
      getStudentWithAssessmentRoutine(params);
      selectByReportSetupYearLevel({
        yearLevelId: formData?.yearLevelId,
        assessmentId: option?.value,
      });

      setSelectAllStudents(false);
      return;
    }

    if (type === "template") {
      setFormData((prev) => ({
        ...prev,
        reportTemplateId: option?.value,
      }));

      if (option?.recruitmentId) {
        setRecruitmentId(option?.recruitmentId);
      }

      const params = {
        assessmentReportTemplateId: option?.value,
        assessmentId: formData?.assessmentId,
        classroomId: formData?.classroomId,
        yearLevelId: formData?.yearLevelId,
      };

      getAssessmentReportValues(params);
      return;
    }
  };

  //handle tabs change
  const handleTabsChange = (yearLevelId) => {
    setActiveTab(yearLevelId);
    setSelectAllStudents(false);
    setFormData({
      assessmentId: "",
      reportTemplateId: "",
      yearLevelId: "",
      classroomId: "",
      educationLevelId: "",
      educationLevelDivisionId: "",
    });
    setIsOpen(false);
    setIsPercent(false);
    setStudents([]);
    setSubjects([]);
  };

  //report generation handler
  const handleReportGenerate = () => {
    if (!formData?.assessmentId || !formData?.reportTemplateId) return;

    const selectedStudents = students
      ?.filter((student) => student?.checked)
      .map((student) => student?.id);

    if (selectedStudents?.length === 0) {
      swal("Oops", "Please select at least one student");
      return;
    }

    if (selectedTemplate?.id === formData?.reportTemplateId) {
      const params = {
        assessmentId: Number(formData?.assessmentId),
        admissionIds: selectedStudents,
        assessmentReportTemplateId: formData?.reportTemplateId,
        isPublish: 0,
        publishDate: selectedPublishedDate,
        isAggregated: isAggregated,
        rank: isPercent ? "percent" : "gpa",
      };

      generateReportTemplate(params);
    } else {
      const params = {
        yearLevelId: Number(formData?.yearLevelId),
        classroomId: Number(formData?.classroomId),
        assessmentId: Number(formData?.assessmentId),
        reportSetupId: Number(formData?.reportTemplateId),
        admissionIds: selectedStudents,
        publishDate: selectedPublishedDate,
        isAggregated: isAggregated,
        rank: isPercent ? "percent" : "gpa",
      };
      generateHtml(params);
    }

    setIsAggregated(false);
    setPublishDate("");
    setSelectedPublishedDate("");
    setConfigModal(false);
    setReportType(null);
    setCalendarMode(localStorage.getItem("calendar-mode"));
    setSelectAllStudents(false);
    setStudents((prev) => {
      const updatedData = prev?.map((item) => ({ ...item, checked: false }));
      return updatedData;
    });
  };

  //report publish handler
  const handlePublishReport = () => {
    if (!formData?.assessmentId || !formData?.reportTemplateId) return;

    const selectedStudents = students
      ?.filter((student) => student?.checked)
      .map((student) => student?.id);

    if (selectedStudents?.length === 0) {
      swal("Oops", "Please select at least one student");
      return;
    }

    if (selectedTemplate?.id === formData?.reportTemplateId) {
      const params = {
        assessmentId: Number(formData?.assessmentId),
        admissionIds: selectedStudents,
        assessmentReportTemplateId: formData?.reportTemplateId,
        isPublish: 1,
        publishDate: selectedPublishedDate,
        isAggregated: isAggregated,
        rank: isPercent ? "percent" : "gpa",
      };

      generateReport(params);
    } else {
      const params = {
        yearLevelId: Number(formData?.yearLevelId),
        classroomId: Number(formData?.classroomId),
        assessmentId: Number(formData?.assessmentId),
        reportSetupId: Number(formData?.reportTemplateId),
        admissionIds: selectedStudents,
        publishDate: selectedPublishedDate,
        isAggregated: isAggregated,
        rank: isPercent ? "percent" : "gpa",
      };

      publishPdf(params);
    }

    setIsAggregated(false);
    setPublishDate("");
    setSelectedPublishedDate("");
    setConfigModal(false);
    setReportType(null);
    setCalendarMode(localStorage.getItem("calendar-mode"));
    setSelectAllStudents(false);
    setStudents((prev) => {
      const updatedData = prev?.map((item) => ({ ...item, checked: false }));
      return updatedData;
    });
  };

  //set First yearleve as active
  useEffect(() => {
    if (finalizedAssessmentsYearLevel?.length > 0) {
      const tab = finalizedAssessmentsYearLevel[0]?.id;
      setActiveTab(tab);
    }
  }, [finalizedAssessmentsYearLevel]);

  //select data according to calendar mode
  useEffect(() => {
    let date =
      calendarMode === "bs" ? publishDate?.bs?.date : publishDate?.ad?.date;
    if (date) {
      setSelectedPublishedDate(date);
    }
  }, [publishDate, calendarMode]);

  return (
    <>
      {/* loader */}
      {(reportGenerating || reportPublishing) && (
        <div className="fullWindow-Spinner">
          <div>
            <Spinner color="white"></Spinner>
          </div>
          <div style={{ fontSize: "16px", marginTop: "15px" }}>
            Please Wait. Processing...
          </div>
        </div>
      )}

      <div className="arr-content">
        <div className="arr-heading">
          <h1>Publish Assessment Report</h1>
          <PermissionButton
            widgetName={name}
            header={"Publish Assessment Report"}
            moduleName={"Reports"}
            activityName={"publish-assessment-report"}
          />
        </div>

        <div>
          {loading ? (
            <div className="new-spinner-container">
              <div className="new-spinner">
                <div className="loader">
                  <Spinner color="primary" />
                </div>
                <div style={{ fontSize: "16px", marginTop: "15px" }}>
                  Please wait !! Processing.....
                </div>
              </div>
            </div>
          ) : finalizedAssessmentsYearLevel?.length > 0 ? (
            <div>
              <div style={{ flexWrap: "wrap" }} className="arr-tabs">
                {finalizedAssessmentsYearLevel?.map((yearLevel) => (
                  <Button
                    key={yearLevel.id}
                    onClick={() => handleTabsChange(yearLevel.id)}
                    variant={activeTab === yearLevel.id ? "primary" : "outline"}
                  >
                    Class {yearLevel.name}
                  </Button>
                ))}
              </div>

              {activeTab &&
                classrooms?.length &&
                classrooms?.map((classroom) => (
                  <div
                    key={classroom.id}
                    style={{ marginTop: "16px" }}
                    className="arr-accordion"
                  >
                    <div
                      style={{
                        justifyContent: "space-between",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        handleOpenAccordion(classroom.id);
                        setFormData((prev) => ({
                          ...prev,
                          classroomId: classroom.id,
                          yearLevelId: classroom.yearLevelId,
                        }));
                      }}
                      className="flex"
                    >
                      <p style={{ margin: "0" }}>{classroom?.name}</p>
                      <FontAwesomeIcon fontSize={14} icon={faChevronDown} />
                    </div>

                    {isOpen === classroom.id && (
                      <>
                        <div
                          className="arr-form noShadow"
                          style={{
                            marginTop: "16px",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            className="flex"
                            style={{
                              alignItems: "center",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <Select
                              value={
                                assessments?.find(
                                  (opt) => opt.value === formData.assessmentId,
                                ) || null
                              }
                              onChange={(option) =>
                                handleSelectChange(option, "assessments")
                              }
                              styles={{ control: { width: "300px" } }}
                              options={assessments}
                              placeholder={"Select Assessment"}
                              isClearable={false}
                            />
                            <Select
                              options={templateOptions}
                              value={
                                templateOptions
                                  ?.flatMap((group) => group.options || [])
                                  .find(
                                    (option) =>
                                      option?.value ===
                                      formData?.reportTemplateId,
                                  ) || null
                              }
                              onChange={(option) =>
                                handleSelectChange(option, "template")
                              }
                              isClearable={false}
                              styles={{ control: { width: "300px" } }}
                              placeholder={"Select Report Setup"}
                              isDisabled={!formData.assessmentId}
                            />
                          </div>
                          <div
                            style={{ flexWrap: "wrap" }}
                            className="action-btns"
                          >
                            <ReportLedger
                              students={students}
                              subjects={subjects}
                              reportValues={reportValues}
                              isPercent={isPercent}
                              printInfo={printInfo}
                              assessmentId={formData.assessmentId}
                            />
                            <Button
                              onClick={() => {
                                setConfigModal(true);
                                setReportType("generate");
                              }}
                              disabled={!formData?.reportTemplateId}
                              variant="outline"
                            >
                              Generate Report
                            </Button>

                            {(checkWidgetAdminOrNot(name) ||
                              Number(recruitmentId) ===
                                Number(loggedInRecruitmentId?.id)) && (
                              <Button
                                onClick={() => {
                                  setConfigModal(true);
                                  setReportType("publish");
                                }}
                                disabled={!formData?.reportTemplateId}
                                variant="outline"
                              >
                                Publish Report
                              </Button>
                            )}
                          </div>
                        </div>
                        {selectedTemplate?.description && (
                          <p className="arr-note">
                            <strong>Note: </strong>{" "}
                            {selectedTemplate?.description}
                          </p>
                        )}
                        {subjects?.length > 0 && (
                          <ReportTable
                            subjects={subjects}
                            students={students}
                            isPercent={isPercent}
                            reportValues={reportValues}
                            selectAllStudents={selectAllStudents}
                            isStudentsLoading={isStudentsLoading}
                            setStudents={setStudents}
                            setSelectAllStudents={setSelectAllStudents}
                            setIsPercent={setIsPercent}
                          />
                        )}
                      </>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="empty-data">No Data found</div>
          )}
        </div>
      </div>

      <CustomModal
        isOpen={configModal}
        toggle={() => setConfigModal(false)}
        modalHeader={"Configure Report Settings"}
        children={
          <div className="report-publish-date">
            <NepaliDatePicker
              styles={{ width: "100%" }}
              onDateSelect={(date) => {
                setPublishDate(date);
              }}
              showToggle={true}
              showNationalHoliday={false}
              calendarMode={calendarMode}
              onCalendarModeChange={(mode) => {
                setCalendarMode(mode);
              }}
              value={publishDate?.ad?.date}
              placeholder={"Select publish date"}
              disabled={!formData?.reportTemplateId}
            />

            {hasAggregation && (
              <div style={{ marginTop: "20px" }}>
                <label>Allow Aggregation</label>
                <Switch
                  className="arr-switch"
                  checked={isAggregated}
                  onClick={(e) => setIsAggregated((prev) => !prev)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              </div>
            )}

            <div style={{ marginTop: "20px" }} className="action-btns end">
              <Button
                onClick={() => {
                  setConfigModal(false);
                  setReportType(null);
                  setPublishDate("");
                }}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>

              {reportType === "generate" ? (
                <Button
                  size="sm"
                  onClick={handleReportGenerate}
                  disabled={!formData?.reportTemplateId || reportGenerating}
                >
                  Generate
                </Button>
              ) : reportType === "publish" ? (
                <Button
                  size="sm"
                  onClick={handlePublishReport}
                  disabled={!formData?.reportTemplateId || reportPublishing}
                >
                  Publish
                </Button>
              ) : null}
            </div>
          </div>
        }
      />
    </>
  );
};

export default PublishAssessmentReport;
